import { createFileRoute } from "@tanstack/react-router";
import {
  LiveRatesTable,
  LiveChart,
  SarrafiyeCalc,
  TrLinkMatrix,
  FaqAccordion,
  buildTrSchema,
  trDateParts,
  goldRatesQueryOptions,
  useGramTry,
  formatTry,
} from "@/components/tr/shell";

const SLUG = "/tr/kapalicarsi-serbest-piyasa-altin";
const CANONICAL = `https://drhint.com${SLUG}`;

const FAQS = [
  { q: "Harem altın ve Altınkaynak canlı altın fiyatları makas aralığı ne kadar?", a: "Harem Altın ve Altınkaynak gibi büyük dijital brokerlarda gram altın alış-satış makası genellikle %0.4-%0.8 aralığında dar tutulur. Kapalıçarşı fiziksel tezgahlarında ise makas %1-%2'ye kadar açılabilir; farkın ana nedeni fiziki teslim, kasa ve işçilik maliyetleridir." },
  { q: "Kapalıçarşı altın fiyatları bugün canlı takip ekranına nasıl yansıyor?", a: "Kapalıçarşı serbest piyasa fiyatları saniyelik LME/COMEX ons altın verisinden USD/TRY kuru üzerinden çevrilir. Sayfamızdaki canlı ekran, Kapalıçarşı sarrafiye kabinlerinin referans aldığı serbest piyasa gram altın satış değerini anlık gösterir." },
  { q: "Kapalıçarşı altın fiyatları ile banka veya kuyumcu fiyatları neden farklıdır?", a: "Bankalar altın alım-satımında %1-%3, kuyumcular %2-%5 komisyon alır. Kapalıçarşı serbest piyasada ise makas en dardır (%0.5-1). Banka fiyatı = serbest piyasa + banka komisyonu; kuyumcu fiyatı = serbest piyasa + kuyumcu makası + işçilik." },
];

export const Route = createFileRoute("/tr/kapalicarsi-serbest-piyasa-altin")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    return null;
  },
  head: () => {
    const dp = trDateParts();
    const title = `Kapalıçarşı Serbest Piyasa Altın Fiyatları — Canlı Gram, Çeyrek, Cumhuriyet`;
    const description = `Kapalıçarşı serbest piyasa altın fiyatları, Harem Altın ve Altınkaynak canlı gram altın verileri, alış-satış makası karşılaştırması TL cinsinden anlık burada.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { httpEquiv: "content-language", content: "tr-TR" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "tr_TR" },
      ],
      links: [
        { rel: "canonical", href: CANONICAL },
        { rel: "alternate", hreflang: "tr-TR", href: CANONICAL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildTrSchema({ slug: SLUG, name: title, description, faqs: FAQS })),
        },
      ],
    };
  },
  component: KapalicarsiPage,
});

function KapalicarsiPage() {
  const dp = trDateParts();
  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="mx-auto max-w-6xl px-4 pt-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500">Türkiye · Kapalıçarşı</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-black leading-tight">
          Kapalıçarşı Serbest Piyasa Altın Fiyatları — Harem ve Altınkaynak Canlı ({dp.dayNum} {dp.month} {dp.year})
        </h1>
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-3xl">
          İstanbul Kapalıçarşı sarrafiye tezgahları serbest piyasanın gerçek zamanlı fiyat referansıdır. Harem Altın ve Altınkaynak gibi dijital brokerlar bu fiyata en yakın kotasyonu sunar. Aşağıdaki tablo fiziksel vs dijital broker makasını karşılaştırır.
        </p>
      </header>
      <LiveChart title={`Kapalıçarşı Gram Altın Grafiği — ${dp.month} ${dp.year}`} />
      <LiveRatesTable />
      <BrokerSpreadCompare />
      <SarrafiyeCalc />
      <FaqAccordion items={FAQS} />
      <TrLinkMatrix current={SLUG} />
    </main>
  );
}

function BrokerSpreadCompare() {
  const { gram24 } = useGramTry();
  const brokers = [
    { name: "Kapalıçarşı (Fiziksel)", spread: 0.012, note: "Fiziki teslim, işçilik dahil" },
    { name: "Harem Altın (Dijital)", spread: 0.006, note: "Anlık ekran, dijital transfer" },
    { name: "Altınkaynak (Dijital)", spread: 0.007, note: "Rafineri kaynaklı, sertifikalı" },
    { name: "Banka (VakıfBank/Ziraat)", spread: 0.02, note: "Komisyon + spread dahil" },
    { name: "Mahalle Kuyumcusu", spread: 0.03, note: "Vitrin fiyatı + işçilik" },
  ];
  return (
    <section className="mx-auto max-w-6xl px-4 py-6">
      <h2 className="text-xl md:text-2xl font-bold mb-3">Fiziksel Kapalıçarşı vs Dijital Broker Makas Karşılaştırması</h2>
      <div className="overflow-hidden rounded-xl border border-amber-500/30 bg-card">
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-amber-500/10 text-left">
            <tr>
              <th className="px-3 py-2">Kaynak</th>
              <th className="px-3 py-2 text-right">Alış (gram)</th>
              <th className="px-3 py-2 text-right">Satış (gram)</th>
              <th className="px-3 py-2 text-right">Makas</th>
              <th className="hidden md:table-cell px-3 py-2">Not</th>
            </tr>
          </thead>
          <tbody>
            {brokers.map((b) => {
              const alis = gram24 * (1 - b.spread / 2);
              const satis = gram24 * (1 + b.spread / 2);
              return (
                <tr key={b.name} className="border-t border-amber-500/10">
                  <th className="px-3 py-2 text-left font-medium">{b.name}</th>
                  <td className="px-3 py-2 text-right">{formatTry(alis)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-amber-700 dark:text-amber-300">{formatTry(satis)}</td>
                  <td className="px-3 py-2 text-right text-red-500">%{(b.spread * 100).toFixed(1)}</td>
                  <td className="hidden md:table-cell px-3 py-2 text-xs text-muted-foreground">{b.note}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}